import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  ThemeProvider,
} from '@mui/material';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import AuthLayout, { AuthLink } from './components/AuthLayout';
import { currentFeatures } from './services/features';
import { theme } from './theme';

// Wave 20.49h — RequireAuth redirects unauth'd visitors to
// /login?next=<path>. After a successful sign-in, honor that
// param and bounce back to where they started. Refuse anything
// that's not a same-origin relative path so the query can't be
// abused as an open redirect.
function readNextPath(): string | null {
  try {
    const p = new URLSearchParams(window.location.search).get('next');
    if (!p) return null;
    if (!p.startsWith('/') || p.startsWith('//')) return null;
    return p;
  } catch {
    return null;
  }
}

export default function Login(_props: { theme?: any }) {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Wave 20.25 — surfaces the resend-verify affordance when the login
  // 403's with EMAIL_NOT_VERIFIED. Value = the email server told us
  // to resend to (already-lowercased / normalized by the DB lookup).
  const [needsVerify, setNeedsVerify] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);

  const handleResend = async () => {
    if (!needsVerify) return;
    setResending(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}/users/verify/email/${encodeURIComponent(needsVerify)}`,
      );
      if (!res.ok) throw new Error('resend failed');
      setResent(true);
    } catch {
      setErrorMsg('Could not resend the verification email. Try again in a moment.');
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || '');
    if (token) {
      try {
        const decoded: JwtPayload = jwtDecode(token);
        if (decoded.exp && new Date(decoded.exp * 1000) > new Date()) {
          window.location.href = readNextPath() ?? '/account';
        }
      } catch {
        /* invalid token — ignore */
      }
    }
  }, []);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email || !password) {
      setErrorMsg(t('login.errorMissing'));
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    setNeedsVerify(null);
    setResent(false);

    fetch(`${process.env.REACT_APP_API}/auth/login/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Trim here too: the server folds case and whitespace, but an
      // older API build in front of a newer client should still work.
      body: JSON.stringify({ email: email.trim(), password }),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok || (data.statusCode && data.statusCode > 201)) {
          // Wave 20.25 — carry the raw body up so the catch can
          // recognize the not-verified case by its code and show the
          // resend UI instead of a plain error string.
          const err: any = new Error(data.message || 'Login failed');
          err.data = data;
          throw err;
        }
        return data;
      })
      .then((user) => {
        localStorage.setItem(
          process.env.REACT_APP_TOKEN_KEY || '',
          user.accessToken
        );
        // Wave 20.43 — commercial riders land on Aero Wake; Rapha
        // (features.wake=false) keeps the existing /session flow.
        // Wave 20.49h — but if RequireAuth redirected them here
        // with ?next=, honor that first so they land back where
        // they were trying to go.
        window.location.href =
          readNextPath() ?? (currentFeatures().wake ? '/wake' : '/session');
      })
      .catch((e) => {
        if (e?.data?.code === 'EMAIL_NOT_VERIFIED') {
          setNeedsVerify(e.data.email || email);
          setLoading(false);
          return;
        }
        // Wave 20.49ct — a 401 means the credentials didn't match.
        // Decide by status, not by the server's message text, so
        // (a) the copy is localised and (b) an older API that still
        // says a bare "Unauthorized" never reaches the form.
        const msg = e instanceof TypeError && e.message === 'Failed to fetch'
          ? t('login.errorNetwork')
          : e?.data?.statusCode === 401 || e.message === 'Unauthorized'
            ? t('login.errorCredentials')
            : e.message || t('login.errorGeneric');
        setErrorMsg(msg);
        setLoading(false);
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <AuthLayout
        title={t('login.title')}
        subtitle={t('login.subtitle')}
        footerLeft={
          <span style={{ color: '#8B95A8' }}>
            {t('login.newHere')} <AuthLink href="/register">{t('login.createAccount')}</AuthLink>
          </span>
        }
      >
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMsg}
          </Alert>
        )}
        {needsVerify && (
          <Alert
            severity={resent ? 'success' : 'warning'}
            sx={{ mb: 3 }}
            action={
              !resent && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleResend}
                  disabled={resending}
                >
                  {resending ? '…' : 'Resend'}
                </Button>
              )
            }
          >
            {resent
              ? `A fresh verification link is on its way to ${needsVerify}. Click it, then sign in again.`
              : `Verify your email first. We sent a link to ${needsVerify}. If you didn't get it, hit Resend.`}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label="Email or username"
            type="text"
            name="email"
            autoComplete="username"
            // Wave 20.51 — This field takes an email OR a username, so
            // it can't be type="email" — and a type="text" input is
            // auto-capitalised by mobile browsers. That turned
            // `rider@x.com` into `Rider@x.com` and, before the server
            // folded case, into "Incorrect username or password".
            inputProps={{
              autoCapitalize: 'none',
              autoCorrect: 'off',
              spellCheck: false,
              inputMode: 'email',
            }}
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2.5 }}
          />
          <TextField
            fullWidth
            label={t('login.password')}
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box sx={{ textAlign: 'right', mb: 3 }}>
            <AuthLink href="/password/reset/request">{t('login.forgotPassword')}</AuthLink>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ py: 1.5, fontSize: '1rem' }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: '#0A0E1A' }} />
            ) : (
              t('login.signIn')
            )}
          </Button>
        </Box>
      </AuthLayout>
    </ThemeProvider>
  );
}
